/**
 * Monitoring bootstrap: Sentry crash reporting + shared error/event helpers.
 *
 * Sentry only activates when `VITE_SENTRY_DSN` is set, so development and
 * preview builds stay silent while production reports automatically.
 */
import * as Sentry from '@sentry/react';

type Primitive = string | number | boolean | null | undefined;
export type EventProps = Record<string, Primitive>;

interface MonitoringUser {
  id: string;
  role?: string;
  plan?: string;
}

const isDev = import.meta.env.DEV;
const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const ENVIRONMENT =
  (import.meta.env.VITE_APP_ENV as string | undefined) ?? (isDev ? 'development' : 'production');
const RELEASE =
  (import.meta.env.VITE_APP_VERSION as string | undefined) ?? `nextchamp@${__APP_VERSION__}`;

let currentUser: MonitoringUser | null = null;
let initialized = false;

export const sentryEnabled = Boolean(DSN);

/** Attach global handlers once, at app startup. */
export function initMonitoring() {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  if (DSN) {
    Sentry.init({
      dsn: DSN,
      environment: ENVIRONMENT,
      release: RELEASE,
      // Breadcrumbs (clicks, navigation, fetch/XHR) are on by default; keep
      // network + console breadcrumbs so Supabase/API failures carry context.
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.breadcrumbsIntegration({ console: true, fetch: true, xhr: true, dom: true, history: true }),
      ],
      tracesSampleRate: ENVIRONMENT === 'production' ? 0.2 : 1.0,
      attachStacktrace: true,
      sendDefaultPii: false,
      beforeSend(event) {
        // Never ship auth tokens or emails to the issue tracker.
        if (event.request?.headers) delete event.request.headers.Authorization;
        return event;
      },
    });
  }

  window.addEventListener('error', (event) => {
    reportError(event.error ?? event.message, { source: 'window.onerror' });
  });

  window.addEventListener('unhandledrejection', (event) => {
    reportError(event.reason, { source: 'unhandledrejection' });
  });
}

/** Identify the signed-in user for every subsequent report. */
export function setMonitoringUser(user: MonitoringUser | null) {
  currentUser = user;
  if (!DSN) return;
  Sentry.setUser(user ? { id: user.id } : null);
  if (user) Sentry.setTags({ role: user.role ?? 'unknown', plan: user.plan ?? 'free' });
}

/** Crash reporting entry point. */
export function reportError(error: unknown, context: EventProps = {}) {
  const payload = {
    message: error instanceof Error ? error.message : String(error),
    stack: error instanceof Error ? error.stack : undefined,
    userId: currentUser?.id,
    ...context,
  };
  if (DSN) {
    Sentry.withScope((scope) => {
      Object.entries(context).forEach(([key, value]) => scope.setExtra(key, value));
      if (typeof context.source === 'string') scope.setTag('source', context.source);
      Sentry.captureException(error instanceof Error ? error : new Error(payload.message));
    });
  }
  if (isDev) console.error('[monitoring] error', payload);
}

/** Non-fatal signal (Supabase/API/Storage/Realtime failures, validation, etc.). */
export function reportWarning(message: string, context: EventProps = {}) {
  if (DSN) {
    Sentry.withScope((scope) => {
      Object.entries(context).forEach(([key, value]) => scope.setExtra(key, value));
      Sentry.captureMessage(message, 'warning');
    });
  }
  if (isDev) console.warn('[monitoring] warning', message, context);
}

/** Product analytics entry point (breadcrumb + Firebase Analytics via lib/analytics). */
export function trackEvent(name: string, props: EventProps = {}) {
  const payload = { name, userId: currentUser?.id, ...props };
  if (DSN) Sentry.addBreadcrumb({ category: 'app', message: name, level: 'info', data: props });
  if (isDev) console.debug('[monitoring] event', payload);
}

/** Performance monitoring entry point (durations in ms). */
export function trackTiming(name: string, durationMs: number, props: EventProps = {}) {
  trackEvent('timing', { metric: name, duration_ms: Math.round(durationMs), ...props });
}
